import { apiClient } from './client';

// Mirrors packages/shared/src/types/gtm.ts (web app keeps its own copy, as funnel.ts does).

export interface GtmScorecard {
  key: string;
  label: string;
  value: number;
  unit: 'count' | 'percent' | 'currency';
  previousValue: number | null;
  deltaPercent: number | null;
  /** Length of the current window in days — drives the "vs prior N days" label. */
  periodDays: number;
  /** Per-week average for count/currency flow metrics; null for rate metrics (CTR). */
  weeklyAverage?: number | null;
  baselineValue?: number | null;
  vsBaselinePercent?: number | null;
  /** One more line under the tile, from the source (e.g. "63 bookings incl. 5 repeats"). */
  detail?: string | null;
}

export interface GtmSearchTrendPoint {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface GtmSearchTrend {
  points: GtmSearchTrendPoint[];
  relaunchDate: string;
}

export interface GtmBrandTrendPoint {
  date: string;
  brandedClicks: number;
  nonBrandedClicks: number;
}

export interface GtmBrandShareOfSearchRow {
  brand: string;
  isOwn: boolean;
  volume: number;
  share: number;
}

export interface GtmBrandSplit {
  branded: { clicks: number; impressions: number };
  nonBranded: { clicks: number; impressions: number };
  brandedClickShare: number;
  trend: GtmBrandTrendPoint[];
  shareOfSearch: GtmBrandShareOfSearchRow[] | null;
}

export interface GtmAuthorityEngine {
  engineId: string;
  engineLabel: string;
  visibility: number | null;
  mentionCount: number;
  isAiOverview: boolean;
}

export interface GtmAuthorityTrendPoint {
  date: string;
  visibility: number | null;
}

export interface GtmAuthorityCompetitor {
  brandId: string;
  brandName: string;
  isOwn: boolean;
  visibility: number | null;
  shareOfVoice: number | null;
}

export interface GtmAuthorityMarket {
  countryCode: string;
  visibility: number | null;
}

export interface GtmAuthority {
  visibility: number | null;
  shareOfVoice: number | null;
  sentiment: number | null;
  position: number | null;
  mentionCount: number;
  /** `domains` names our own cited domain(s); Peec counts domains, not pages. */
  citation: { retrievalCount: number; citationCount: number; sourceCount: number; domains?: string[] };
  perEngine: GtmAuthorityEngine[];
  aiOverviewVisibility: number | null;
  trend: GtmAuthorityTrendPoint[];
  competitors: GtmAuthorityCompetitor[];
  byMarket: GtmAuthorityMarket[];
  relaunchDate: string;
}

/** Pipeline created in the period for one channel: deals opened in the window, open + won. */
export interface GtmSourcedPipelineRow {
  channel: string;
  label: string;
  pipelineValue: number;
  wonValue: number;
  /** Lost value of the deals created in the window, left out of pipelineValue. */
  lostValue: number;
  dealCount: number;
  /** "unknown" row only: the open + won deals with no channel, to fix in Attio. */
  deals?: GtmUnattributedDeal[];
}

/** A deal counted in "No channel recorded": no company, or a company with no lead channel. */
export interface GtmUnattributedDeal {
  name: string;
  value: number;
  companyName: string | null;
  /** Attio deal record URL; null when the deal has no Attio record id. */
  attioUrl: string | null;
}

/** One decisive item behind a deal's source (an inbound form, an outbound touch, a sales field, the deal type). */
export interface GtmDealSourceEvidence {
  kind: 'inbound_conversion' | 'outbound_touch' | 'lead_source' | 'account_source' | 'deal_type';
  /** ISO timestamp; null for a field value. */
  at: string | null;
  /** Form / campaign / UTM, rep / sender, or the field value. */
  label: string;
  email: string | null;
}

/** A deal created in the period, with where it came from and why. */
export interface GtmDealSourceDeal {
  name: string;
  companyName: string | null;
  value: number;
  stage: string;
  createdAt: string;
  group: 'new_business' | 'expansion_renewal';
  /** Null for expansion & renewal: those are not sourced. */
  motion: string | null;
  /** Did marketing source the deal, touch it earlier, or nothing we can see. */
  role: 'sourced' | 'claimed' | 'influenced' | 'none';
  /** The rule that decided it. */
  decidedBy: string;
  evidence: GtmDealSourceEvidence[];
  /** Attio type missing or "Automation": counted as new business by default. */
  untyped: boolean;
  /** A sales field claims inbound but an outbound touch exists in the window (shown in `evidence`). */
  conflict: boolean;
  /** Why the motion is unknown on purpose (e.g. an ambiguous LinkedIn field). */
  needsClarification: string | null;
  attioUrl: string | null;
}

/** New business for one go-to-market motion (open + won deals created in the period). */
export interface GtmDealSourceMotionRow {
  motion: string;
  label: string;
  pipelineValue: number;
  wonValue: number;
  dealCount: number;
  deals: GtmDealSourceDeal[];
}

/** Pipeline created in the period by how each deal started, decided from touches and sales fields (see attribution/deal-source.ts). */
export interface GtmDealSources {
  /** New business by motion, largest first, "unknown" last. */
  newBusiness: GtmDealSourceMotionRow[];
  newBusinessValue: number;
  newBusinessDeals: number;
  expansionRenewal: {
    pipelineValue: number;
    wonValue: number;
    dealCount: number;
    /** Part of the value with an earlier marketing touch. */
    influencedValue: number;
    deals: GtmDealSourceDeal[];
  };
  /** New-business inbound deals with a tracked conversion behind them (headline). */
  sourcedValue: number;
  sourcedDeals: number;
  /** New-business deals a sales field calls inbound (Lead/Account Source) with no tracked conversion. */
  claimedValue: number;
  claimedDeals: number;
  /** Claimed deals with an outbound touch in the window, to check. */
  claimedConflictDeals: number;
  /** An evidence source came back empty for the lookback window, so some touches cannot be seen. */
  dataWarnings: string[];
  /** Deals marketing touched earlier but did not source (ABM, outbound-first, expansion/renewal). */
  influencedValue: number;
  influencedDeals: number;
  /** Share of new-business value whose motion is known; null with no new business. */
  knownSourceShare: number | null;
  /** New-business deals with no Attio type (or "Automation"), counted as new by default. */
  untypedDeals: number;
  untypedValue: number;
  /** Lost value of the deals created in the period, left out of every figure above. */
  lostValue: number;
  lookbackMonths: number;
  /** The day the attribution logic changed, for a marker on any trend. */
  attributionChangedDate: string;
}

export interface GtmVerticalRow {
  /** Attio Industry / ICP category, "Other (not ICP)" or "Unclassified". */
  vertical: string;
  fit?: 'icp' | 'not_icp' | 'unclassified';
  pipelineValue: number;
  dealCount: number;
  leadCount: number;
}

export interface GtmIcpByVertical {
  rows: GtmVerticalRow[];
  /** ICP rows over all pipeline created / all leads. */
  icpFitPipelineShare: number;
  icpFitLeadShare: number;
  unclassifiedPipelineShare?: number;
  unclassifiedLeadShare?: number;
}

export interface GtmMarketRow {
  country: string;
  clicks: number;
  impressions: number;
  sessions: number;
}

export interface GtmBoardData {
  period: { startDate: string; endDate: string };
  relaunchDate: string;
  scorecards: GtmScorecard[];
  searchTrend: GtmSearchTrend | null;
  brandSplit: GtmBrandSplit | null;
  authority: GtmAuthority | null;
  sourcedPipeline: GtmSourcedPipelineRow[] | null;
  /** Pipeline created by how each deal started, with the evidence per deal. */
  dealSources: GtmDealSources | null;
  icpByVertical: GtmIcpByVertical | null;
  markets: GtmMarketRow[] | null;
  unavailable: string[];
  unavailableReasons?: Record<string, string>;
}

export interface GtmDemoFunnelPeriod {
  occurred: number;
  noShows: number | null;
  canceled: number;
  demoPageViews: number | null;
}

export interface GtmDemoFunnel {
  thisMonth: GtmDemoFunnelPeriod;
  lastMonth: GtmDemoFunnelPeriod;
  upcoming: number;
}

export interface GtmFixQueueRow {
  page: string;
  impressions: number;
  clicks: number;
  ctr: number;
  position: number;
}

export interface GtmQueryMovementRow {
  query: string;
  clicks: number;
  impressions: number;
  position: number;
  previousPosition: number;
  positionDelta: number;
  ctr: number;
  previousCtr: number;
  ctrDelta: number;
}

export interface GtmBuyerIntentRow {
  companyDomain: string | null;
  companyName: string | null;
  signalCount: number;
  categoryCount: number;
  /** G2 category names, sorted; older APIs omit it. */
  categories?: string[];
  lastSeen: string;
}

export interface GtmLinkedInPostRow {
  contentItemId: string;
  title: string;
  publishAt: string | null;
  impressions: number;
  engagements: number;
  engagementRate: number | null;
}

export interface GtmLinkedInOrganic {
  totals: { posts: number; impressions: number; engagements: number; avgEngagementRate: number | null };
  topPosts: GtmLinkedInPostRow[];
}

export interface GtmMarketConversionRow {
  country: string;
  sessions: number;
  /** Every lead form (website, webinar, LinkedIn) from this country's companies. */
  leads: number;
  /** Website-form leads only, as in the funnel (webinar sign-ups and LinkedIn forms out). Absent on older API builds. */
  websiteLeads?: number;
  /** websiteLeads / sessions; on older API builds leads / sessions. */
  conversionRate: number | null;
}

export interface GtmEmailCampaignRow {
  id: number;
  name: string;
  kind: 'campaign' | 'newsletter';
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  /** Opens Customer.io counts as human. */
  humanOpened: number;
  /** Clicks Customer.io counts as human (scanner clicks left out). */
  humanClicked: number;
  converted: number;
  unsubscribed: number;
  /** humanOpened / delivered. */
  openRate: number | null;
  /** humanClicked / delivered. */
  clickRate: number | null;
}

export interface GtmEmailPerformance {
  totals: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    humanOpened: number;
    humanClicked: number;
    converted: number;
    unsubscribed: number;
    openRate: number | null;
    clickRate: number | null;
  };
  campaigns: GtmEmailCampaignRow[];
  /** Campaign figures only cover from this day (Customer.io keeps 45 days); null when the window is covered. */
  campaignsCoveredFrom: string | null;
}

export interface GtmPaidCampaignRow {
  platform: 'linkedin' | 'google' | 'meta' | 'bing';
  campaignId: string;
  name: string;
  status: string | null;
  spend: number;
  impressions: number;
  clicks: number;
  leads: number | null;
  costPerLead: number | null;
  /** Form leads from paid matched to this campaign. */
  formLeads?: number;
  /** Spend from Odoo's monthly vendor bills rather than the platform's campaign data (Bing). */
  spendSource?: 'odoo_bills';
  /** How that spend was derived. */
  spendNote?: string;
}

export interface GtmPaidEfficiency {
  campaigns: GtmPaidCampaignRow[];
  totals: {
    spend: number;
    impressions: number;
    clicks: number;
    /** Platform-reported conversions. */
    leads: number;
    /** Form leads attributed to paid (GtmLeadFunnel.paidLeads). */
    formLeads: number | null;
    /** Paid form leads matched to no campaign row. */
    unmatchedFormLeads?: number;
    /** spend / formLeads. */
    costPerLead: number | null;
    wonValue: number | null;
    roas: number | null;
    /** Why Bing's Odoo-bill spend could not be loaded. */
    bingSpendUnavailable?: string;
  };
}

export interface GtmUtmSourceRow {
  source: string;
  medium: string | null;
  count: number;
  /** Set on a "(none)" source row: the person's first-party first touch, used when the form carried no UTM. */
  firstTouchFallback?: string;
}

export interface GtmMarketingAttribution {
  totalSubmissions: number;
  utmSources: GtmUtmSourceRow[];
  /** People behind those submissions: the funnel's leads. */
  leads?: number;
  /** Left out by the funnel's rule: the same numbers the funnel reports. */
  excluded?: { tests: number; otherFormSubmissions: number };
  /** Submissions with no UTM source whose source row carries `firstTouchFallback` (first-party first touch). */
  firstTouchResolved?: number;
  /** Submissions with no UTM source and no first-party touch to fall back on: still unknown. */
  unknownSource?: number;
}

/** The Overview's one lead definition: people who submitted a lead form (website or LinkedIn) in the period. */
export interface GtmLeadFunnel {
  sessions: number | null;
  leads: number;
  mql: number;
  sql: number;
  customer: number;
  websiteFormLeads: number;
  linkedinFormLeads: number;
  /** Leads in both of the above (sent a website form and a LinkedIn lead form). */
  bothSources?: number;
  byForm: { form: string; source: 'website' | 'linkedin'; leads: number }[];
  /** People who sent more than one website lead form (counted under their first). */
  multiFormLeads?: number;
  paidLeads: number;
  paidByPlatform: { platform: 'linkedin' | 'google' | 'meta' | 'bing' | 'other'; leads: number; viaLeadForm?: number }[];
  noCountry: number;
  excluded: { tests: number; otherFormSubmissions: number };
}

/** The four MQL types Lea set the target on (demo bookings, trial requests, webinar registrations, pricing requests). */
export type GtmMqlType = 'demo' | 'trial' | 'webinar' | 'pricing';

export interface GtmMqlTypeRow {
  type: GtmMqlType;
  label: string;
  /** Every non-test submission of this type, once per person per month. */
  raw: number;
  /** Raw that passes the qualification rule (`GtmMqlReport.qualificationRule`). */
  qualified: number;
}

/** One calendar month the selected period touches, with its share of the monthly target. */
export interface GtmMqlMonthRow {
  /** YYYY-MM. */
  month: string;
  /** Days of the month inside the period. */
  days: number;
  daysInMonth: number;
  /** Monthly target x days / daysInMonth. */
  target: number;
  raw: number;
  qualified: number;
  byType: { type: GtmMqlType; raw: number; qualified: number }[];
}

/** MQLs against the monthly target (Q4 2026: 120 qualified a month). Not the funnel's "Lead to deal opened" step. */
export interface GtmMqlReport {
  monthlyTarget: number;
  targetLabel: string;
  /** The target for the period: the monthly target prorated by days, summed over the months. */
  target: number;
  rawTotal: number;
  qualifiedTotal: number;
  byType: GtmMqlTypeRow[];
  months: GtmMqlMonthRow[];
  /** Why raw MQLs did not qualify (first failed check), largest first. */
  disqualified: { reason: string; label: string; count: number }[];
  /** Demo MQLs that came only from a Calendly booking (no demo form behind them). */
  calendlyOnlyDemos: number;
  /** The qualification rule, one line per check, shown verbatim in the info tip. */
  qualificationRule: string[];
  /** False when the competitor registry gave no domains, so the competitor check did nothing. */
  competitorCheck: boolean;
  /** Lead forms that are not an MQL type: today the LinkedIn lead gen forms (content downloads). */
  other: {
    leads: number;
    spend: number | null;
    costPerLead: number | null;
    /** `campaigns_with_leads`: spend of the LinkedIn campaigns that produced the leads; `linkedin_total`: all LinkedIn spend. */
    spendBasis: 'campaigns_with_leads' | 'linkedin_total' | null;
    spendNote: string | null;
  };
}

/** Counted demos in the period by how the booking started (Sales' source classification). */
export interface GtmDemoBookingSources {
  total: number;
  bySource: { source: string; label: string; count: number }[];
}

export interface GtmMarketingData {
  period: { startDate: string; endDate: string };
  leadFunnel: GtmLeadFunnel | null;
  demoFunnel: GtmDemoFunnel | null;
  fixQueue: GtmFixQueueRow[] | null;
  queryMovement: { improved: GtmQueryMovementRow[]; declined: GtmQueryMovementRow[] } | null;
  buyerIntent: GtmBuyerIntentRow[] | null;
  linkedinOrganic: GtmLinkedInOrganic | null;
  marketConversion: GtmMarketConversionRow[] | null;
  emailPerformance: GtmEmailPerformance | null;
  paidEfficiency: GtmPaidEfficiency | null;
  attribution: GtmMarketingAttribution | null;
  /** MQLs against the monthly target (null when the lead set is unavailable). */
  mqlReport?: GtmMqlReport | null;
  /** Counted demos by booking source, same window and count as the "Demos booked" tile. */
  demoBookingSources?: GtmDemoBookingSources | null;
  unavailable: string[];
  unavailableReasons?: Record<string, string>;
}

export async function getGtmBoard(startDate?: string, endDate?: string): Promise<GtmBoardData> {
  return apiClient.get<GtmBoardData>('/gtm/board', {
    params: { startDate, endDate },
  });
}

export async function getGtmMarketing(startDate?: string, endDate?: string): Promise<GtmMarketingData> {
  return apiClient.get<GtmMarketingData>('/gtm/marketing', {
    params: { startDate, endDate },
  });
}

export interface GtmClusterCoverageRow {
  cluster: string;
  market: string;
  keywordCount: number;
  avgPosition: number | null;
  page1Count: number;
  /** Total monthly search volume across the cluster's keywords (size of prize). */
  searchVolume: number | null;
}

export interface GtmQuickWinRow {
  query: string;
  position: number;
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface GtmDemandShiftRow extends GtmQuickWinRow {
  /** Impressions in the period before. */
  previousImpressions?: number;
}

export interface GtmContentGapRow {
  query: string;
  impressions: number;
  position: number;
  searchVolume: number | null;
}

export interface GtmGlossaryRow {
  page: string;
  clicks: number;
  impressions: number;
  position: number;
}

export interface GtmAiCitationGapRow {
  promptId: string;
  promptText: string;
  topic: string | null;
  visibility: number | null;
  volume: string | null;
}

export interface GtmCitedSourceRow {
  domain: string;
  url: string | null;
  title: string | null;
  classification: string | null;
  retrievalCount: number;
  citationCount: number;
  mentionedBrandCount: number;
}

export interface GtmIcpTrafficRow {
  pagePath: string;
  totalVisits: number;
  icpVisits: number;
  icpShare: number;
  topIcpCompanies: string[];
}

export interface GtmContentConversionRow {
  pagePath: string;
  convertingVisitors: number;
  totalVisitors: number | null;
}

export interface GtmContentData {
  period: { startDate: string; endDate: string };
  clusterCoverage: GtmClusterCoverageRow[] | null;
  quickWins: GtmQuickWinRow[] | null;
  contentGaps: GtmContentGapRow[] | null;
  glossary: GtmGlossaryRow[] | null;
  demandShifts: { emerging: GtmDemandShiftRow[]; declining: GtmDemandShiftRow[] } | null;
  aiCitationGaps: GtmAiCitationGapRow[] | null;
  citedSources: GtmCitedSourceRow[] | null;
  icpTraffic: GtmIcpTrafficRow[] | null;
  /** What the ICP-fit traffic rule saw: identified companies, how many met it, how many had no RB2B headcount. */
  icpTrafficBasis?: { companies: number; icpCompanies: number; noSize: number; nonIcpNames: string[] } | null;
  contentConversion: GtmContentConversionRow[] | null;
  unavailable: string[];
}

export interface GtmHealthScorecard {
  key: string;
  label: string;
  value: number | null;
  unit: 'count' | 'percent' | 'number';
  source: string;
  /** Snapshot day for counts that are a state on one day (Semrush), not a period total. */
  asOf?: string | null;
}

export interface GtmBacklinkPoint {
  date: string;
  referringDomains: number | null;
  backlinks: number | null;
  newBacklinks: number | null;
  lostBacklinks: number | null;
}

export interface GtmAgentVisitRow {
  botId: string | null;
  botName: string | null;
  visits: number;
}

export interface GtmIndexCoveragePage {
  url: string;
  verdict: string | null;
  coverageState: string | null;
  indexed: boolean;
}

export interface GtmIndexCoverage {
  sampled: number;
  indexed: number;
  notIndexed: number;
  excluded: number;
  pages: GtmIndexCoveragePage[];
}

export interface GtmWebVitalRow {
  url: string;
  formFactor: string;
  lcpMs: number | null;
  inpMs: number | null;
  cls: number | null;
  overall: string | null;
  capturedDate: string;
}

export interface GtmLighthouseOpportunity {
  id: string;
  title: string;
  displayValue: string | null;
  savingsMs: number | null;
}

export interface GtmLighthouseRow {
  url: string;
  formFactor: string;
  perfScore: number | null;
  a11yScore: number | null;
  bestPracticesScore: number | null;
  seoScore: number | null;
  lcpMs: number | null;
  tbtMs: number | null;
  clsLab: number | null;
  opportunities: GtmLighthouseOpportunity[];
  capturedDate: string;
}

export interface GtmSiteAudit {
  capturedDate: string | null;
  errors: number | null;
  warnings: number | null;
  notices: number | null;
  healthScore: number | null;
  crawlErrors: number | null;
  redirectIssues: number | null;
  hreflangIssues: number | null;
}

export interface GtmBotAccessRow {
  bot: string;
  vendor: string;
  allowed: boolean;
  matchedRule: string | null;
}

export interface GtmHealthData {
  period: { startDate: string; endDate: string };
  scorecards: GtmHealthScorecard[];
  backlinkTrend: GtmBacklinkPoint[] | null;
  agentVisits: GtmAgentVisitRow[] | null;
  botAccess: GtmBotAccessRow[] | null;
  indexCoverage: GtmIndexCoverage | null;
  webVitals: GtmWebVitalRow[] | null;
  lighthouse: GtmLighthouseRow[] | null;
  siteAudit: GtmSiteAudit | null;
  unavailable: string[];
}

export async function getGtmContent(startDate?: string, endDate?: string): Promise<GtmContentData> {
  return apiClient.get<GtmContentData>('/gtm/content', { params: { startDate, endDate } });
}

export async function getGtmHealth(startDate?: string, endDate?: string): Promise<GtmHealthData> {
  return apiClient.get<GtmHealthData>('/gtm/health', { params: { startDate, endDate } });
}

export async function classifyIcpFit(): Promise<{ classified: number; dealsUpdated: number }> {
  return apiClient.post<{ classified: number; dealsUpdated: number }>('/gtm/icp-fit/classify');
}

// ── Content calendar (Peec proposals only) ───────────────────────────────────

export interface ContentCalendarProposal {
  id: string;
  proposedDate: string;
  peecScope: string | null;
  domain: string | null;
  opportunityScore: number | null;
  gapPercentage: number | null;
  title: string;
  rationale: string | null;
  suggestedContentType: string;
  funnelStage: string | null;
  status: string;
  contentItemId: string | null;
}

export interface ContentCalendarData {
  proposals: ContentCalendarProposal[];
}

export interface DraftResult {
  contentItemId: string;
  jobId: string | null;
  contentType: string;
}

export async function getContentCalendar(from: string, to: string): Promise<ContentCalendarData> {
  return apiClient.get<ContentCalendarData>('/gtm/content-calendar', { params: { from, to } });
}

export async function draftProposal(id: string): Promise<DraftResult> {
  return apiClient.post<DraftResult>(`/gtm/content-calendar/proposals/${id}/draft`);
}

export async function dismissProposal(id: string): Promise<{ ok: boolean }> {
  return apiClient.post<{ ok: boolean }>(`/gtm/content-calendar/proposals/${id}/dismiss`);
}
